import { MoldOrder } from '../types/mold';
import { Section, Question, DetailFieldConfig } from '../types/form';
import { StepConfig } from '../types/steps';
import {
  buildResolveContext,
  isAllowedOrderType,
  resolveVisibleFields,
  resolveVisibleQuestions,
} from './formVisibility';
import { getConditionalAttachmentOrderDetailKeys } from './form';

// Required photo tags (same as in original code)
export const REQUIRE_PHOTO_TAGS = [
  'before_core',
  'before_cavity',
  'after_core',
  'after_cavity',
];

const isEmptyArray = (value: any): boolean => {
  return Array.isArray(value) && value.length === 0;
};

/** A currently-uploaded file as seen by the questionnaire (tag-keyed). */
export type CurrentFile = { tag?: string; tags?: string[] };

const countFilesWithTag = (
  currentFiles: CurrentFile[],
  tag?: string,
): number => {
  if (!tag) return 0;
  let count = 0;
  for (const file of currentFiles) {
    if (
      file.tag === tag ||
      (Array.isArray(file.tags) && file.tags.includes(tag))
    )
      count++;
  }
  return count;
};

// Counts attachment evidence from the form side-keys OR the live file list (by tag),
// max of the two — so copy-carried files (which carry the tag but write no side-key)
// satisfy the requirement without double-counting normally-uploaded files that have both.
function isAttachmentRequirementMet(
  question: Question,
  formData: Record<string, any>,
  currentFiles: CurrentFile[] = [],
): boolean {
  const cond = question.conditionalAttachment;
  if (!cond || !cond.attachmentConfig) return true;
  const cfg = cond.attachmentConfig as any;
  if (!cfg.required) return true;

  const { photoOrderDetailKey: imageOrderDetailKey, fileOrderDetailKey } =
    getConditionalAttachmentOrderDetailKeys(question);
  const imageAttachments = formData?.[imageOrderDetailKey] || [];
  const fileAttachments = formData?.[fileOrderDetailKey] || [];
  // default min per type
  const min = 1;
  const counts = {
    image: Math.max(
      imageAttachments.length,
      countFilesWithTag(currentFiles, cfg.photoConfig?.tag),
    ),
    document: Math.max(
      fileAttachments.length,
      countFilesWithTag(currentFiles, cfg.fileConfig?.tag),
    ),
  };

  const mode =
    cfg.attachmentType ||
    (cfg.photoConfig && 'photo') ||
    (cfg.fileConfig && 'file') ||
    'photo';

  switch (mode) {
    case 'photo':
      return counts.image >= (cfg.photoConfig?.minPhotoCount || min);
    case 'file':
      return counts.document >= (cfg.fileConfig?.minFileCount || min);
    case 'both':
      // require both image and document
      return (
        counts.image >= (cfg.photoConfig?.minPhotoCount || min) &&
        counts.document >= (cfg.fileConfig?.minFileCount || min)
      );
    case 'choice':
    default:
      // satisfied if either type meets minimum
      return (
        counts.image >= (cfg.photoConfig?.minPhotoCount || min) ||
        counts.document >= (cfg.fileConfig?.minFileCount || min)
      );
  }
}

export interface ProgressData {
  percentage: number;
  answeredQuestions: number;
  unansweredQuestions: number;
  totalQuestions: number;
  requiredQuestions: number;
  answeredRequiredQuestions: number;
  unansweredRequiredQuestions: number;
  requiredQuestionsPercentage: number;
  isRequiredComplete: boolean;
  ngCount?: number;
  isComplete?: boolean;
  hasNG?: boolean;
}

interface ValidationIssue {
  sectionId: string;
  sectionTitle: string;
  questionId: string;
  questionLabel: string; // Using label instead of title for Question objects
  message: string;
}

export const isFieldAnswered = (
  fieldId: string,
  field: DetailFieldConfig,
  fields: Record<string, any>,
  formData: Record<string, any>,
) => {
  const value = fields[fieldId];
  if (field.type === 'before-after-images') {
    if (
      field.beforeConfig?.orderDetailKey &&
      field.afterConfig?.orderDetailKey
    ) {
      const uploadedImages = formData[field.beforeConfig.orderDetailKey];
      const afterImages = formData[field.afterConfig.orderDetailKey];
      return (
        uploadedImages &&
        afterImages &&
        uploadedImages.length > 0 &&
        afterImages.length > 0
      );
    }
    return false;
  } else {
    return (
      (value !== undefined && value !== null && value !== '') || field.disabled
    );
  }
};

/**
 * Calculate progress for a set of questions
 * This function is used internally by calculateFormProgress and calculateProgressForVisibleQuestions
 *
 * @param questions - Array of questions to calculate progress for
 * @param formData - The form data object
 * @returns Object with progress metrics
 */
function collectResolvedVisibleQuestions(
  sections: Section[],
  formData: Record<string, any>,
  moldOrder: MoldOrder,
): Question[] {
  return resolveVisibleQuestions(
    { sections },
    buildResolveContext(moldOrder),
    formData,
  )
    .filter((resolved) => resolved.visible)
    .map((resolved) => resolved.question);
}

function calculateQuestionsProgress(
  questions: Question[],
  formData: Record<string, any>,
  currentFiles: CurrentFile[] = [],
): {
  totalQuestions: number;
  answeredQuestions: number;
  requiredQuestions: number;
  answeredRequiredQuestions: number;
  ngCount: number;
  progress: number;
  requiredQuestionsPercentage: number;
  isRequiredComplete: boolean;
  isComplete: boolean;
  hasNG: boolean;
} {
  let totalQuestions = 0;
  let answeredQuestions = 0;
  let requiredQuestions = 0;
  let answeredRequiredQuestions = 0;
  let ngCount = 0;

  questions.forEach((q: Question) => {
    if (q.type === 'details') {
      const fields = formData[q.id] || {};
      const filteredFields = resolveVisibleFields(q, formData).filter(
        ({ field, visible }) => {
          return (
            visible &&
            field &&
            field.type !== 'images' &&
            field.type !== 'computed'
          );
        },
      );

      filteredFields.forEach(({ fieldId, field }) => {
        totalQuestions++;
        const isAnswered = isFieldAnswered(fieldId, field, fields, formData);
        if (isAnswered) answeredQuestions++;

        // Count required fields
        if (field.required) {
          requiredQuestions++;
          if (isAnswered) answeredRequiredQuestions++;
        }
      });
    } else if (q.type === 'details-array') {
      const rows = formData[q.id] || [];
      if (rows.length > 0) {
        rows.forEach((row: Record<string, any>) => {
          // details-array `showIf` reads the TOP-LEVEL answers, not the row — the
          // documented resolver divergence DetailsFieldsRenderer renders by.
          const nonImageFields = resolveVisibleFields(q, formData).filter(
            ({ field, visible }) => {
              return (
                visible &&
                field &&
                field.type !== 'images' &&
                field.type !== 'before-after-images'
              );
            },
          );

          nonImageFields.forEach(({ fieldId, field }) => {
            totalQuestions++;
            const value = row[fieldId];
            const isAnswered =
              value !== undefined && value !== null && value !== '';
            if (isAnswered) answeredQuestions++;

            // Count required fields
            if (field.required) {
              requiredQuestions++;
              if (isAnswered) answeredRequiredQuestions++;
            }
          });
        });
      } else {
        totalQuestions++;
        if (q.required) {
          requiredQuestions++;
        }
      }
    } else if (q.type === 'entity-list') {
      if (q.fields && q.fields.length > 0) {
        const questionToAnswerKeys = q.fields
          .filter((f) => !f.readonly)
          .map((f) => f.id);
        const totalEntities = Object.values(formData[q.id] || {}) || [];
        totalEntities.forEach((entity: any) => {
          questionToAnswerKeys.forEach((key: string) => {
            totalQuestions++;
            const isAnswered =
              entity[key] !== undefined &&
              entity[key] !== null &&
              entity[key] !== '' &&
              !isEmptyArray(entity[key]);
            if (isAnswered) answeredQuestions++;

            // For entity-list, we can consider all questions as required or add logic here
            // For now, treating entity-list items as required if the question itself is marked as required
            if (q.required) {
              requiredQuestions++;
              if (isAnswered) answeredRequiredQuestions++;
            }
          });
        });
      }
    } else if (q.type === 'checkbox-group') {
      totalQuestions++;
      const options = formData[q.id] || {};
      const isAnswered = Object.values(options).some((val) => val);
      if (isAnswered) answeredQuestions++;

      // Count as required if question is marked as required
      if (q.required) {
        requiredQuestions++;
        if (isAnswered) answeredRequiredQuestions++;
      }
    } else {
      // Count this question as one logical unit
      totalQuestions++;

      // Determine if answered using centralized logic
      const answered = isQuestionAnswered(q, formData, currentFiles);
      if (answered) answeredQuestions++;

      // Count as required if question is marked as required
      if (q.required) {
        requiredQuestions++;
        if (answered) answeredRequiredQuestions++;
      }

      // Count NG for radio questions
      if (q.type === 'radio' && formData[q.id] === 'NG') {
        ngCount++;
      }
    }
  });

  return {
    totalQuestions,
    answeredQuestions,
    requiredQuestions,
    answeredRequiredQuestions,
    ngCount,
    progress:
      totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0,
    requiredQuestionsPercentage:
      requiredQuestions > 0
        ? (answeredRequiredQuestions / requiredQuestions) * 100
        : 100,
    isRequiredComplete:
      requiredQuestions === 0 ||
      answeredRequiredQuestions === requiredQuestions,
    isComplete:
      requiredQuestions > 0
        ? answeredRequiredQuestions === requiredQuestions
        : true,
    hasNG: ngCount > 0,
  };
}

/** The zero/empty ProgressData used by the early-return guards. */
export function emptyProgressData(): ProgressData {
  return {
    percentage: 0,
    answeredQuestions: 0,
    unansweredQuestions: 0,
    totalQuestions: 0,
    requiredQuestions: 0,
    answeredRequiredQuestions: 0,
    unansweredRequiredQuestions: 0,
    requiredQuestionsPercentage: 100,
    isRequiredComplete: true,
    ngCount: 0,
    isComplete: false,
    hasNG: false,
  };
}

/** Shape a calculateQuestionsProgress tally into the public ProgressData. */
function toProgressData(
  progress: ReturnType<typeof calculateQuestionsProgress>,
): ProgressData {
  return {
    percentage: progress.progress,
    answeredQuestions: progress.answeredQuestions,
    unansweredQuestions: progress.totalQuestions - progress.answeredQuestions,
    totalQuestions: progress.totalQuestions,
    requiredQuestions: progress.requiredQuestions,
    answeredRequiredQuestions: progress.answeredRequiredQuestions,
    unansweredRequiredQuestions:
      progress.requiredQuestions - progress.answeredRequiredQuestions,
    requiredQuestionsPercentage: progress.requiredQuestionsPercentage,
    isRequiredComplete: progress.isRequiredComplete,
    ngCount: progress.ngCount,
    isComplete: progress.isComplete,
    hasNG: progress.hasNG,
  };
}

/**
 * Calculate the overall progress of a form
 *
 * @param formData - The form data object
 * @param sections - Array of form sections
 * @param moldOrder - The mold order object
 * @returns ProgressData object with percentage and question counts
 */
export function calculateFormProgress(
  formData: Record<string, any>,
  sections: Section[],
  moldOrder: MoldOrder,
  currentFiles: CurrentFile[] = [],
): ProgressData {
  if (!formData || sections.length === 0) {
    return emptyProgressData();
  }

  const filteredQuestions = collectResolvedVisibleQuestions(
    sections,
    formData,
    moldOrder,
  );

  // Calculate progress using the existing helper function. calculateQuestionsProgress
  // already tallies radio NG over this same resolver-filtered `filteredQuestions` set
  // (and from it derives hasNG), so re-deriving ngCount via a second full
  // resolveVisibleQuestions traversal here was redundant work yielding the identical
  // value — and left ngCount/hasNG sourced inconsistently. Use the single tally.
  const progress = calculateQuestionsProgress(
    filteredQuestions,
    formData,
    currentFiles,
  );

  return toProgressData(progress);
}

/**
 * Calculate the progress of a section from its ALREADY-visibility-filtered questions.
 *
 * The caller passes the questions the single resolver marked visible for this section
 * (Questionnaire's isQuestionVisible / visibleQuestionKeys), so section progress is
 * counted against the SAME visible set the form renders — instead of a second
 * shouldShowQuestion pass that could drift from the resolver. See ADR-001 §2.3 (arity
 * drift) and 02-current-state-map.md §4 rule 6.
 *
 * @param visibleQuestions - The questions resolved visible for the section
 * @param formData - The form data object
 * @returns ProgressData object with percentage and question counts
 */
export function calculateProgressForVisibleQuestions(
  visibleQuestions: Question[],
  formData: Record<string, any>,
): ProgressData {
  if (!formData) {
    return emptyProgressData();
  }
  const progress = calculateQuestionsProgress(visibleQuestions, formData);
  return toProgressData(progress);
}

/**
 * Calculate progress including required photos
 *
 * @param formData - The form data object
 * @param sections - Array of form sections
 * @param moldOrder - The mold order object
 * @param missingFilesTags - Array of required photo tags that are missing
 * @returns ProgressData object with percentage and question counts including photo requirements
 */
export function calculateProgressWithPhotos(
  formData: Record<string, any>,
  sections: Section[],
  moldOrder: MoldOrder,
  missingFilesTags: string[],
): ProgressData {
  const progressResult = calculateFormProgress(formData, sections, moldOrder);
  const {
    answeredQuestions: answeredQuestionsForm,
    totalQuestions: totalQuestionsForm,
  } = progressResult;

  const totalPhotos = REQUIRE_PHOTO_TAGS.length;
  const totalQuestions = totalQuestionsForm + totalPhotos;
  const answeredQuestions =
    answeredQuestionsForm + totalPhotos - missingFilesTags.length;
  const percentage =
    totalQuestions > 0
      ? Math.round((answeredQuestions / totalQuestions) * 100)
      : 0;

  return {
    ...progressResult,
    percentage,
    totalQuestions,
    answeredQuestions,
    unansweredQuestions: totalQuestions - answeredQuestions,
    isComplete: totalQuestions === answeredQuestions,
  };
}

/**
 * Check if a question has been answered
 *
 * @param question - The question to check
 * @param formData - The form data object
 * @returns boolean indicating if the question is answered
 */
function isQuestionAnswered(
  question: Question,
  formData: Record<string, any>,
  currentFiles: CurrentFile[] = [],
): boolean {
  // Skip if formData doesn't exist
  if (!formData) return false;

  switch (question.type) {
    case 'radio':
      // radio must have a value
      if (!formData[question.id]) return false;
      // if conditional attachment is required and trigger value active, ensure attachments exist per config
      if (
        question.conditionalAttachment &&
        question.conditionalAttachment.attachmentConfig
      ) {
        const triggerVal = question.conditionalAttachment.triggerValue;
        if (
          typeof triggerVal !== 'undefined' &&
          formData[question.id] === triggerVal
        ) {
          return isAttachmentRequirementMet(question, formData, currentFiles);
        }
      }
      return true;

    case 'checkbox-group':
      return (
        Array.isArray(formData[question.id]) && formData[question.id].length > 0
      );

    case 'details':
      if (!question.fields) return false;

      // Check if all required fields are filled
      if (!question.fields) return false;

      let allRequiredFieldsFilled = true;
      // Use Object.values to convert the fields object to an array
      const fieldValues = Object.values(question.fields);
      for (const field of fieldValues) {
        if (!field.required) continue;

        const fieldValue = formData[`${question.id}_${field.label}`];
        if (field.type === 'images' || field.type === 'before-after-images') {
          if (!(Array.isArray(fieldValue) && fieldValue.length > 0)) {
            allRequiredFieldsFilled = false;
            break;
          }
        } else if (!fieldValue) {
          allRequiredFieldsFilled = false;
          break;
        }
      }
      return allRequiredFieldsFilled;

    case 'entity-list':
      // For entity lists, check if at least one entity has required fields filled
      if (!formData[question.id] || typeof formData[question.id] !== 'object') {
        return false;
      }

      // Check if any entity has data
      return Object.keys(formData[question.id]).length > 0;

    case 'details-array':
      // Similar to details but for array type fields
      if (!formData[question.id] || !Array.isArray(formData[question.id])) {
        return false;
      }

      return formData[question.id].length > 0;

    default:
      return false;
  }
}

/**
 * Check if a form can be submitted based on completion criteria
 *
 * @param formData - The form data object
 * @param sections - Array of form sections
 * @param moldOrder - The mold order object
 * @param requiredPercentage - Optional percentage threshold for submission (default: 100)
 * @returns Object with canSubmit boolean and reasons for rejection
 */
export function canSubmitForm(
  formData: Record<string, any>,
  sections: Section[],
  moldOrder: MoldOrder,
  requiredPercentage: number = 100,
): { canSubmit: boolean; validationIssues: ValidationIssue[] } {
  // Calculate current progress
  const progress = calculateFormProgress(formData, sections, moldOrder);
  const validationIssues: ValidationIssue[] = [];

  // Check if progress meets the required percentage
  if (progress.percentage < requiredPercentage) {
    const sectionTitleById = new Map(
      sections.map((section) => [section.id, section.title]),
    );
    const resolvedQuestions = resolveVisibleQuestions(
      { sections },
      buildResolveContext(moldOrder),
      formData,
    );

    resolvedQuestions.forEach(({ question, sectionId, visible }) => {
      if (!visible) return;

      if (!isQuestionAnswered(question, formData)) {
        validationIssues.push({
          sectionId,
          sectionTitle: sectionTitleById.get(sectionId) || '',
          questionId: question.id,
          questionLabel: question.label,
          message: 'คำถามนี้ยังไม่ได้ตอบ',
        });
      }
    });
  }

  return {
    canSubmit: progress.percentage >= requiredPercentage,
    validationIssues,
  };
}

/**
 * Get validation issues that would prevent form submission
 *
 * @param moldOrder - The mold order object
 * @param loadedFormConfig - The loaded form configuration with sections
 * @param requiredPercentage - Optional percentage threshold for submission (default: 100)
 * @returns Array of validation issues
 */
export function getValidationIssues(
  moldOrder: MoldOrder,
  loadedFormConfig: { sections: Section[] },
  requiredPercentage: number = 100,
): ValidationIssue[] {
  const { sections } = loadedFormConfig;
  const formData = moldOrder.orderDetail?.form || {};

  // First check if form is complete
  const { canSubmit, validationIssues } = canSubmitForm(
    formData,
    sections,
    moldOrder,
    requiredPercentage,
  );

  if (!canSubmit) {
    return validationIssues;
  }

  // Add additional validation logic here if needed

  return [];
}

// Helper to check if question should be shown based on dependsOn
export const shouldShowQuestion = (
  question: Question,
  formData: Record<string, any>,
  moldOrderType?: string,
): boolean => {
  if (!isAllowedOrderType(question?.allowedOrderTypes, moldOrderType)) {
    return false;
  }
  if (!question?.dependsOn) return true;

  const { questionId, equals, notEquals, equalsAny } = question.dependsOn as {
    questionId: string;
    equals?: any;
    notEquals?: any;
    equalsAny?: any[];
  };
  // Backward compatibility: if only 'equals' is present, use old logic
  if (typeof equals !== 'undefined') {
    return formData[questionId] === equals;
  }
  // New: if 'notEquals' is present, show if value is NOT equal
  if (typeof notEquals !== 'undefined') {
    return formData[questionId] !== notEquals;
  }
  // New: if 'equalsAny' is present, show if value is in the array
  if (Array.isArray(equalsAny)) {
    return equalsAny.includes(formData[questionId]);
  }
  // Default fallback: show
  return true;
};

export const shouldShowSection = (
  section: Section,
  moldOrderType: string,
): boolean => {
  // section.allowedOrderTypes may include main types or similar; keep simple check
  return isAllowedOrderType(section.allowedOrderTypes, moldOrderType);
};

/**
 * Extract required tags from visible BEFORE_UPLOAD and AFTER_UPLOAD steps
 *
 * @param visibleSteps - Array of already filtered visible step configurations
 * @param moldOrderType - The mold order type for filtering
 * @returns Array of required tags
 */
function extractRequiredTagsFromVisibleSteps(
  visibleSteps: StepConfig[],
  moldOrderType: string,
): string[] {
  if (!Array.isArray(visibleSteps)) return [];

  const requiredTags: string[] = [];

  // Filter visible steps with BEFORE_UPLOAD or AFTER_UPLOAD component types
  const uploadSteps = visibleSteps.filter(
    (step) =>
      step.componentType === 'BEFORE_UPLOAD' ||
      step.componentType === 'AFTER_UPLOAD',
  );

  uploadSteps.forEach((step) => {
    const photoChecklistConfig = step.props?.photoChecklistConfig;
    if (
      !photoChecklistConfig?.items ||
      !Array.isArray(photoChecklistConfig.items)
    ) {
      return;
    }

    // Filter items by allowedOrderTypes and extract tags
    photoChecklistConfig.items.forEach((item: any) => {
      // Include item if no allowedOrderTypes specified, or if moldOrderType is in allowedOrderTypes
      const shouldInclude =
        !item.allowedOrderTypes ||
        !Array.isArray(item.allowedOrderTypes) ||
        item.allowedOrderTypes.includes(moldOrderType);

      if (shouldInclude) {
        if (step.props?.prefix && item.id) {
          const itemTag = `${step.props.prefix}_${item.id}`;
          requiredTags.push(itemTag);
        } else {
          console.error(
            'Step is missing prefix or item is missing id:',
            step,
            item,
          );
        }
      }
    });
  });

  return requiredTags;
}

/**
 * Get validation messages that describe issues with the current progress
 *
 * @param progress - The current progress data
 * @param loadedFormConfig - The loaded form configuration with sections and optional images/steps
 * @param currentFiles - The list of currently uploaded files
 * @param moldOrder - The mold order object
 * @param requiredPercentage - Optional percentage threshold for submission (default: 100)
 * @returns Array of validation messages
 */
export function getValidationMessagesFromProgress(
  progress: ProgressData,
  loadedFormConfig: {
    sections: Section[];
    images?: any[];
    steps?: any[];
    visibleSteps?: StepConfig[];
  },
  currentFiles: CurrentFile[],
  moldOrder: MoldOrder,
): string[] {
  const messages: string[] = [];

  let requiredTags: string[] = [];
  if (
    loadedFormConfig?.visibleSteps &&
    Array.isArray(loadedFormConfig.visibleSteps) &&
    loadedFormConfig.visibleSteps.length > 0
  ) {
    requiredTags = extractRequiredTagsFromVisibleSteps(
      loadedFormConfig.visibleSteps,
      moldOrder.type,
    );
  } else if (loadedFormConfig?.images) {
    const images = loadedFormConfig.images;
    requiredTags = Array.isArray(images)
      ? images
          .filter((img: any) =>
            // legacy images: use the original type without conversion
            isAllowedOrderType(img.allowedOrderTypes, moldOrder?.type),
          )
          .flatMap((img: { tags: string[] }) => img.tags || [])
      : [];
  }
  // Build present tags set
  const presentTags = new Set<string>();
  (currentFiles || []).forEach((file) => {
    if (file.tag) presentTags.add(file.tag);
    if (Array.isArray(file.tags)) file.tags.forEach((t) => presentTags.add(t));
  });

  const missingFilesTags = requiredTags.filter((tag) => !presentTags.has(tag));

  if (missingFilesTags.length > 0) {
    const beforeFiles = missingFilesTags.filter((tag) =>
      tag.includes('before_'),
    );

    const afterFiles = missingFilesTags.filter((tag) => tag.includes('after_'));

    if (beforeFiles.length > 0) {
      const message = `กรุณาอัปโหลดไฟล์ที่จำเป็น: รูปก่อนดำเนินการ (${beforeFiles.length} รูป)`;
      messages.push(message);
    }
    if (afterFiles.length > 0) {
      const message = `กรุณาอัปโหลดไฟล์ที่จำเป็น: รูปหลังดำเนินการ (${afterFiles.length} รูป)`;
      messages.push(message);
    }
  }
  // Check unanswered questions only when progress below requiredPercentage OR if unansweredQuestions > 0
  if (progress.unansweredRequiredQuestions > 0) {
    messages.push(
      `พบคำถามบังคับที่ยังไม่ได้กรอก ${progress.unansweredRequiredQuestions} ข้อ`,
    );
  }

  return messages;
}
